import { getEmployeeDetails } from "../../api/employeesApi";
import { getWorkPlaceTimezone } from "../../api/workplaceAPi";
import styles from "./clock-time.module.scss";
import { useEffect, useState } from "react";
import { OverlayTrigger, Popover, Spinner } from "react-bootstrap";
import infoIcon from "../../assets/info-circle.svg";

const ClockTime = () => {
  const formattedTime = () => {
    const now = new Date();
    return now.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
  };

  const [currentTime, setCurrentTime] = useState(formattedTime());
  const [employee, setEmployeeData] = useState(null);
  const [workplaceZone, setWorkplaceZone] = useState({});
  const [isLoading, setLoading] = useState(true);

  const [totalBreaks, setTotalBreaks] = useState(0);
  const [totalPaidMealBreak, setTotalPaidMealBreak] = useState(0);
  const [totalUnPaidMealBreak, setTotalUnPaidMealBreak] = useState(0);
  const [totalPaidRestBreak, setTotalPaidRestBreak] = useState(0);
  const [totalUnPaidResBreak, setTotalUnPaidRestBreak] = useState(0);

  const [togglePaidMealBreak, setTogglePaidMealBreak] = useState(false);
  const [toggleUnpaidMealBreak, setToggleUnpaidMealBreak] = useState(false);
  const [togglePaidRestBreak, setTogglePaidRestBreak] = useState(false);
  const [toggleUnpaidRestBreak, setToggleUnpaidRestBreak] = useState(false);

  const [clockIn, setClockIn] = useState("");
  const [clockOut, setClockOut] = useState("");
  const [paidMealBreak, setPaidMealBreak] = useState("");
  const [unpaidMealBreak, setUnpaidMealBreak] = useState("");
  const [paidRestBreak, setPaidRestBreak] = useState("");
  const [unpaidRestBreak, setUnpaidRestBreak] = useState("");

  const [paidMealBreakOut, setPaidMealBreakOut] = useState("");
  const [unpaidMealBreakOut, setUnpaidMealBreakOut] = useState("");
  const [paidRestBreakOut, setPaidRestBreakOut] = useState("");
  const [unpaidRestBreakOut, setUnpaidRestBreakOut] = useState("");

  const convertMilliToTimeFormat = (time) => {
    const totalSeconds = Math.floor(time / 1000);

    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    return `${hours}hr ${minutes}m ${seconds}s`;
  };

  const parseTimeToMilliseconds = (timeStr) => {
    const today = new Date().toDateString();
    const dateTime = new Date(`${today} ${timeStr}`);

    const hours = dateTime.getHours();
    const minutes = dateTime.getMinutes();
    const seconds = dateTime.getSeconds();

    // Total milliseconds since midnight
    const totalMs = (hours * 3600 + minutes * 60 + seconds) * 1000;

    return totalMs;
  };

  const breaks = [
    { in: paidMealBreak, out: paidMealBreakOut, label: "Paid Meal" },
    { in: paidRestBreak, out: paidRestBreakOut, label: "Paid" },
    { in: unpaidMealBreak, out: unpaidMealBreakOut, label: "Unpaid Meal" },
    { in: unpaidRestBreak, out: unpaidRestBreakOut, label: "Unpaid Rest" },
  ];

  const popover = (
    <Popover id="breaks-popover" style={{ minWidth: "max-content" }}>
      <Popover.Body className="p-2">
        {breaks.map((b, index) => (
          <div key={index} className="py-1 border-bottom">
            <div className="d-flex justify-content-between">
              <div className="me-3" style={{ whiteSpace: "nowrap" }}>
                <span style={{ color: "#898989" }}>{b.label} Break :</span>{" "}
                <span>{b.in ? convertMilliToTimeFormat(b.in) : "-"}</span>
              </div>
              <div style={{ whiteSpace: "nowrap" }}>
                <span style={{ color: "#898989" }}>{b.label} Break out :</span>{" "}
                <span>{b.out ? convertMilliToTimeFormat(b.out) : "-"}</span>
              </div>
            </div>
          </div>
        ))}
      </Popover.Body>
    </Popover>
  );

  useEffect(() => {
    const intervalId = setInterval(() => {
      setCurrentTime(formattedTime());
    }, 1000);
    return () => clearInterval(intervalId);
  }, [currentTime]);

  const companyId = "com_affa65d93030f0118b3d6045bdc7c627";
  const employeeId = "emp_55b194e8f530f0118b3d6045bdc7c627";

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const res = await getEmployeeDetails(companyId, employeeId);
        if (res?.data && typeof res.data === "object") {
          setEmployeeData(res.data);
        }
      } catch (error) {
        console.error("Error fetching employee details:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await getWorkPlaceTimezone(
          companyId,
          employee?.workplaces[0]?.id
        );
        setWorkplaceZone(res.data);
      } catch (error) {
        console.error("Error fetching employee details:", error);
      }
    };
    if (employee) {
      fetchData();
    }
  }, [employee]);

  useEffect(() => {
    if (
      totalPaidMealBreak ||
      totalPaidRestBreak ||
      totalUnPaidMealBreak ||
      totalUnPaidResBreak
    ) {
      setTotalBreaks(
        totalPaidMealBreak +
          totalUnPaidMealBreak +
          totalPaidRestBreak +
          totalUnPaidResBreak
      );
    }
  }, [
    totalPaidMealBreak,
    totalPaidRestBreak,
    totalUnPaidMealBreak,
    totalUnPaidResBreak,
  ]);

  const today = new Date();
  const formattedDate = new Intl.DateTimeFormat("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
    year: "numeric",
  }).format(today);

  const checkForExistingBreaks = (action) => {
    if (action === "Paid Meal Break") {
      if (
        toggleUnpaidMealBreak ||
        togglePaidRestBreak ||
        toggleUnpaidRestBreak
      ) {
        alert("Please end the active break before starting a new break.");
        return false;
      }
      return true;
    }

    if (action === "Unpaid Meal Break") {
      if (togglePaidMealBreak || togglePaidRestBreak || toggleUnpaidRestBreak) {
        alert("Please end the active break before starting a new break.");
        return false;
      }
      return true;
    }

    if (action === "Paid Rest Break") {
      if (
        togglePaidMealBreak ||
        toggleUnpaidMealBreak ||
        toggleUnpaidRestBreak
      ) {
        alert("Please end the active break before starting a new break.");
        return false;
      }
      return true;
    }

    if (action === "Unpaid Rest Break") {
      if (togglePaidMealBreak || toggleUnpaidMealBreak || togglePaidRestBreak) {
        alert("Please end the active break before starting a new break.");
        return false;
      }
      return true;
    }

    return true;
  };

  const handleButtonClick = (action) => {
    if (action === "Clock In" && clockIn === "") setClockIn(formattedTime());
    if (action === "Clock Out" && clockOut === "") setClockOut(formattedTime());

    if (checkForExistingBreaks(action)) {
      if (action === "Paid Meal Break") {
        setPaidMealBreak(parseTimeToMilliseconds(formattedTime()));
        setTogglePaidMealBreak(!togglePaidMealBreak);
      }

      if (action === "Paid Meal Break out") {
        setPaidMealBreakOut(parseTimeToMilliseconds(formattedTime()));
        setTogglePaidMealBreak(!togglePaidMealBreak);
        setTotalPaidMealBreak(
          totalPaidMealBreak +
            parseInt(
              parseTimeToMilliseconds(formattedTime()) - parseInt(paidMealBreak)
            )
        );
      }

      if (action === "Unpaid Meal Break") {
        setUnpaidMealBreak(parseTimeToMilliseconds(formattedTime()));
        setToggleUnpaidMealBreak(!toggleUnpaidMealBreak);
      }

      if (action === "Unpaid Meal Break out") {
        setUnpaidMealBreakOut(parseTimeToMilliseconds(formattedTime()));
        setToggleUnpaidMealBreak(!toggleUnpaidMealBreak);
        setTotalUnPaidMealBreak(
          totalUnPaidMealBreak +
            parseInt(
              parseTimeToMilliseconds(formattedTime()) -
                parseInt(unpaidMealBreak)
            )
        );
      }

      if (action === "Paid Rest Break") {
        setPaidRestBreak(parseTimeToMilliseconds(formattedTime()));
        setTogglePaidRestBreak(!togglePaidRestBreak);
      }

      if (action === "Paid Rest Break out") {
        setPaidRestBreakOut(parseTimeToMilliseconds(formattedTime()));
        setTogglePaidRestBreak(!togglePaidRestBreak);
        setTotalPaidRestBreak(
          totalPaidRestBreak +
            parseInt(
              parseTimeToMilliseconds(formattedTime()) - parseInt(paidRestBreak)
            )
        );
      }

      if (action === "Unpaid Rest Break") {
        setUnpaidRestBreak(parseTimeToMilliseconds(formattedTime()));
        setToggleUnpaidRestBreak(!toggleUnpaidRestBreak);
      }

      if (action === "Unpaid Rest Break out") {
        setUnpaidRestBreakOut(parseTimeToMilliseconds(formattedTime()));
        setToggleUnpaidRestBreak(!toggleUnpaidRestBreak);
        setTotalUnPaidRestBreak(
          totalUnPaidResBreak +
            parseInt(
              parseTimeToMilliseconds(formattedTime()) -
                parseInt(unpaidRestBreak)
            )
        );
      }
    }
  };

  return (
    <div className={styles["clock-container"]}>
      <div className={styles.timer}>
        <h1>{currentTime}</h1>
        <p>{formattedDate}</p>
      </div>

      <div className={styles["work-employee"]}>
        {isLoading ? (
          <div className={styles["work-employee-details"]}>
            <div className={styles["work-employee-details-load"]}>
              <Spinner animation="border" />
            </div>
          </div>
        ) : (
          <div className={styles["work-employee-details"]}>
            <p>Select Workplace</p>
            <p className={styles.workplace}>Main bar</p>
            <div className={styles.details}>
              <span className={styles.emp}>Workplace Id</span>
              <span className={styles["emp-id"]}>
                {employee?.workplaces[0]?.id
                  ? employee?.workplaces[0]?.id
                  : "N/A"}
              </span>
            </div>
            <div className={styles["main-details"]}>
              <div className={styles["details-1"]}>
                <span className={styles.time}>Timezone</span>
                <span className={styles.data}>
                  {workplaceZone?.timekeepingSettings?.defaultTimeZoneId
                    ? workplaceZone?.timekeepingSettings?.defaultTimeZoneId
                    : "N/A"}
                </span>
              </div>
              <div className={styles["details-1"]}>
                <span className={styles.time}>Address</span>
                <span className={styles.data}>
                  {employee?.workplaces
                    ? employee?.workplaces[0]?.address.city +
                      " " +
                      employee?.workplaces[0]?.address.street1
                    : "N/A"}
                </span>
              </div>
            </div>
          </div>
        )}
        {isLoading ? (
          <div className={styles["work-employee-details"]}>
            <div className={styles["work-employee-details-load"]}>
              <Spinner animation="border" />
            </div>
          </div>
        ) : (
          <div className={styles["work-employee-details"]}>
            <p>Select Employee</p>
            <p className={styles.employee}>
              {employee?.firstName ? employee?.firstName : "Employee"}{" "}
              {employee?.lastName ? employee?.lastName : "name"}
            </p>
            <div className={styles.details}>
              <span className={styles.emp}>Employee Id</span>
              <span className={styles["emp-id"]}>
                {employee?.id ? employee?.id : "N/A"}
              </span>
            </div>
            <div className={styles["main-details"]}>
              <div className={styles["details-1"]}>
                <span className={styles.time}>First Name</span>
                <span className={styles.data}>
                  {employee?.firstName ? employee?.firstName : "N/A"}{" "}
                </span>
              </div>
              <div className={styles["details-1"]}>
                <span className={styles.time}>Last Name</span>
                <span className={styles.data}>
                  {employee?.lastName ? employee?.lastName : "N/A"}
                </span>
              </div>
              <div className={styles["details-1"]}>
                <span className={styles.time}>Start Date</span>
                <span className={styles.data}>
                  {employee?.startDate ? employee?.startDate : "N/A"}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className={styles["shifts-clock"]}>
        <div className={styles.shifts}>
          <div className={styles.container}>
            <div className={styles["shifts-details"]}>
              <span className={styles["shift-head"]}>Shift hours</span>
              <span>
                {clockIn && clockOut
                  ? convertMilliToTimeFormat(
                      parseTimeToMilliseconds(clockOut) -
                        parseTimeToMilliseconds(clockIn)
                    )
                  : "0hr 0m 0s"}
              </span>
            </div>
            <div className={styles["shifts-details"]}>
              <span className={styles["shift-head"]}>Paid hours</span>
              <span>0hr 0m 0s</span>
            </div>
            <div className={styles["shifts-details"]}>
              <span className={styles["shift-head"]}>Paid breaks</span>
              <span>
                {totalPaidMealBreak || totalPaidRestBreak
                  ? convertMilliToTimeFormat(
                      totalPaidMealBreak + totalPaidRestBreak
                    )
                  : ""}
              </span>
            </div>
            <div className={styles["shifts-details"]}>
              <span className={styles["shift-head"]}>Unpaid breaks</span>
              <span>
                {totalUnPaidMealBreak || totalUnPaidResBreak
                  ? convertMilliToTimeFormat(
                      totalUnPaidMealBreak + totalUnPaidResBreak
                    )
                  : ""}
              </span>
            </div>
          </div>
          <div className={styles["clock-details"]}>
            <div className={styles["clock-1"]}>
              <span>Clock-in Time</span>
              <span className={styles.timee}>{clockIn}</span>
            </div>
            <div className={styles["clock-1"]}>
              <span>Clock-out Time</span>
              <span className={styles.timee}>{clockOut}</span>
            </div>
          </div>
          <div className={styles["break-container"]}>
            <span className={styles["spn-1"]}>
              Breaks{" "}
              <OverlayTrigger
                trigger={["hover", "focus"]}
                placement="right"
                overlay={popover}
              >
                <span style={{ cursor: "pointer", color: "#888" }}>
                  <img src={infoIcon} alt="info icon" />
                </span>
              </OverlayTrigger>
            </span>
            <span>
              {totalBreaks
                ? convertMilliToTimeFormat(totalBreaks)
                : "0hr 0m 0s"}
            </span>
          </div>
        </div>

        <div className={styles["time-tracker-container"]}>
          <div className={styles["time-tracker-grid"]}>
            <button
              className={`${styles["time-tracker-button"]} ${styles["clock-in"]}`}
              onClick={() => handleButtonClick("Clock In")}
            >
              CLOCK-IN
            </button>
            <button
              className={`${styles["time-tracker-button"]} ${styles["clock-out"]}`}
              onClick={() => handleButtonClick("Clock Out")}
            >
              CLOCK-OUT
            </button>
            <button
              className={`${styles["time-tracker-button"]} ${styles["paid-meal"]}`}
              onClick={() =>
                handleButtonClick(
                  togglePaidMealBreak
                    ? "Paid Meal Break out"
                    : "Paid Meal Break"
                )
              }
            >
              {togglePaidMealBreak ? "PAID MEAL BREAK OUT" : "PAID MEAL BREAK"}
            </button>
            <button
              className={`${styles["time-tracker-button"]} ${styles["unpaid-meal"]}`}
              onClick={() =>
                handleButtonClick(
                  toggleUnpaidMealBreak
                    ? "Unpaid Meal Break out"
                    : "Unpaid Meal Break"
                )
              }
            >
              {toggleUnpaidMealBreak
                ? " UNPAID MEAL BREAK OUT"
                : " UNPAID MEAL BREAK"}
            </button>
            <button
              className={`${styles["time-tracker-button"]} ${styles["paid-rest"]}`}
              onClick={() =>
                handleButtonClick(
                  togglePaidRestBreak
                    ? "Paid Rest Break out"
                    : "Paid Rest Break"
                )
              }
            >
              {togglePaidRestBreak ? "PAID REST BREAK OUT" : "PAID REST BREAK"}
            </button>
            <button
              className={`${styles["time-tracker-button"]} ${styles["unpaid-rest"]}`}
              onClick={() =>
                handleButtonClick(
                  toggleUnpaidRestBreak
                    ? "Unpaid Rest Break out"
                    : "Unpaid Rest Break"
                )
              }
            >
              {toggleUnpaidRestBreak
                ? "UNPAID REST BREAK OUT"
                : "UNPAID REST BREAK"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClockTime;
